// Equity offer creation form
import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { createClerkSupabaseClient } from '@/lib/supabase'
import { useGetToken } from '@/lib/hooks/use-auth'
import { useProject } from '@/contexts/project-context'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Loader2, Calculator, Info } from 'lucide-react'
import ContributionSnapshot from './contribution-snapshot'
import { EquityCalculationEngine } from '@/lib/equity-calculator-v2'
import { EquityCalculationResult } from '@/types/equity-calculator'

const offerSchema = z.object({
  user_id: z.string().min(1, 'Please select a user'),
  shares_offered: z.number().min(1, 'Please enter number of shares'),
  equity_class_id: z.string().min(1, 'Please select an equity class'),
  vesting_schedule_id: z.string().min(1, 'Please select a vesting schedule'),
  strike_price: z.number().optional(),
  option_type: z.string().optional(),
  exercise_window_months: z.number().optional(),
  has_83b_election: z.boolean().optional(),
  explanation: z.string().min(20, 'Please provide a detailed explanation'),
})

type OfferFormData = z.infer<typeof offerSchema>

interface User {
  id: string
  email: string
  first_name: string | null
  last_name: string | null
}

interface VestingSchedule {
  id: number
  schedule_name: string
  duration_months: number
  cliff_months: number | null
  vesting_frequency: string | null
}

interface EquityClass {
  id: string
  class_name: string
  description: string | null
}

interface EquityOfferFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
  selectedUserId?: string
}

export default function EquityOfferForm({ 
  open, 
  onOpenChange, 
  onSuccess,
  selectedUserId 
}: EquityOfferFormProps) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [users, setUsers] = useState<User[]>([])
  const [vestingSchedules, setVestingSchedules] = useState<VestingSchedule[]>([])
  const [equityClasses, setEquityClasses] = useState<EquityClass[]>([])
  const [latestValuation, setLatestValuation] = useState<any>(null)
  const [shareStats, setShareStats] = useState<any>(null)
  const [contributionData, setContributionData] = useState<any>(null)
  const [equityRecommendation, setEquityRecommendation] = useState<number | null>(null)
  const [showContributions, setShowContributions] = useState(false)
  const { currentProject } = useProject()
  const getToken = useGetToken()

  const form = useForm<OfferFormData>({
    resolver: zodResolver(offerSchema),
    defaultValues: {
      user_id: selectedUserId || '',
      shares_offered: 0,
      equity_class_id: '1', // Default to Common stock
      vesting_schedule_id: '0',
      strike_price: undefined,
      option_type: undefined,
      exercise_window_months: 90,
      has_83b_election: false,
      explanation: '',
    },
  })

  useEffect(() => {
    if (open) {
      fetchData()
    }
  }, [open])

  useEffect(() => {
    if (selectedUserId) {
      form.setValue('user_id', selectedUserId)
      setShowContributions(true)
    }
  }, [selectedUserId, form])

  useEffect(() => {
    if (contributionData && currentProject) {
      calculateRecommendedShares()
    }
  }, [contributionData, currentProject])

  const fetchData = async () => {
    if (!currentProject?.id) return

    try {
      console.log('Fetching equity offer form data...')
      const supabase = createClerkSupabaseClient(() => getToken())

      // Fetch users who don't have pending offers
      const { data: usersData, error: usersError } = await supabase
        .from('users')
        .select('id, email, first_name, last_name')
        .order('first_name')

      if (usersError) {
        console.error('Error fetching users:', usersError)
      } else if (usersData) {
        console.log('Fetched users:', usersData)
        setUsers(usersData)
      }

      // Fetch vesting schedules
      const { data: vestingData, error: vestingError } = await supabase
        .from('vesting_schedules')
        .select('*')
        .order('duration_months')

      if (vestingError) {
        console.error('Error fetching vesting schedules:', vestingError)
      } else if (vestingData) {
        console.log('Fetched vesting schedules:', vestingData)
        // Fix the type mismatch by casting the id to number
        const typedVestingData = vestingData.map(schedule => ({
          ...schedule,
          id: parseInt(schedule.id as string)
        })) as VestingSchedule[]
        setVestingSchedules(typedVestingData)
      }

      // Fetch equity classes
      const { data: classesData, error: classesError } = await supabase
        .from('equity_classes')
        .select('*')
        .order('id')

      if (classesError) {
        console.error('Error fetching equity classes:', classesError)
      } else if (classesData) {
        console.log('Fetched equity classes:', classesData)
        // The database already returns string IDs for equity classes
        setEquityClasses(classesData)
      }

      // Fetch latest valuation
      const { data: valuationData, error: valuationError } = await supabase
        .rpc('get_latest_valuation', {
          p_project_id: currentProject.id,
          p_valuation_type: '409A'
        })

      if (valuationError) {
        console.error('Error fetching valuation:', valuationError)
      } else if (valuationData && valuationData.length > 0) {
        setLatestValuation(valuationData[0])
      }

      // For now, we'll use a simple calculation for outstanding shares
      // This would be replaced with a proper RPC function once it exists
      const { data: offersData, error: offersError } = await supabase
        .from('equity_offers')
        .select('shares_offered, status')
        .eq('project_id', currentProject.id)
        .eq('status', 'accepted')

      if (offersError) {
        console.error('Error fetching offers:', offersError)
      } else if (offersData) {
        const totalOutstandingShares = offersData.reduce((sum, offer) => sum + (offer.shares_offered || 0), 0)
        setShareStats([{ outstanding_shares: totalOutstandingShares }])
      } else {
        setShareStats([{ outstanding_shares: 0 }])
      }
    } catch (error) {
      console.error('Error in fetchData:', error)
      setError(error instanceof Error ? error.message : 'Failed to fetch form data')
    }
  }

  const calculateRecommendedShares = async () => {
    if (!currentProject || !contributionData) return

    try {
      const engine = new EquityCalculationEngine(currentProject.id)
      const result = await engine.calculateEquityAllocations()
      
      // Find the allocation for the selected user
      const userAllocation = result.allocations.find(a => a.user_id === form.watch('user_id'))
      if (userAllocation) {
        setEquityRecommendation(userAllocation.shares)
      }
    } catch (error) {
      console.error('Error calculating recommendation:', error)
    }
  }

  const handleContributionDataLoaded = (data: any) => {
    setContributionData(data)
  }

  const onSubmit = async (data: OfferFormData) => {
    setLoading(true)
    setError(null)

    try {
      if (!currentProject) {
        throw new Error('No project selected')
      }
      
      // Calculate percentage based on shares
      const totalShares = shareStats?.reduce((sum: number, stat: any) => sum + Number(stat.outstanding_shares), 0) || 0
      const newTotalShares = totalShares + data.shares_offered
      const percentage = (data.shares_offered / newTotalShares) * 100

      // Set strike price for options
      let strikePrice = data.strike_price
      if (data.equity_class_id === '3' && !strikePrice && latestValuation) { // Options
        strikePrice = latestValuation.common_share_price
      }

      const { error } = await createClerkSupabaseClient(() => getToken())
        .from('equity_offers')
        .insert({
          user_id: data.user_id,
          shares_offered: data.shares_offered,
          proposed_equity_percentage: percentage,
          equity_class_id: data.equity_class_id,
          vesting_schedule_id: data.vesting_schedule_id,
          strike_price: strikePrice,
          option_type: data.option_type,
          exercise_window_months: data.exercise_window_months,
          has_83b_election: data.has_83b_election,
          valuation_id: latestValuation?.id,
          explanation: data.explanation,
          status: 'pending',
          project_id: currentProject.id,
          recommended_shares: equityRecommendation,
        })

      if (error) throw error

      onSuccess?.()
      onOpenChange(false)
      form.reset()
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const selectedSchedule = vestingSchedules.find(
    vs => vs.id === parseInt(form.watch('vesting_schedule_id'))
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Create Equity Offer</DialogTitle>
          <DialogDescription>
            Make an equity offer to a team member with vesting terms
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
              {error}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="user">Team Member</Label>
            <Select
              value={form.watch('user_id')}
              onValueChange={(value) => form.setValue('user_id', value)}
              disabled={!!selectedUserId}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a team member" />
              </SelectTrigger>
              <SelectContent>
                {users.map((user) => (
                  <SelectItem key={user.id} value={user.id}>
                    {user.first_name || ''} {user.last_name || ''} ({user.email})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {form.formState.errors.user_id && (
              <p className="text-sm text-red-600">{form.formState.errors.user_id.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="shares">Number of Shares</Label>
              {equityRecommendation && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => form.setValue('shares_offered', equityRecommendation)}
                  className="text-xs"
                >
                  <Calculator className="h-3 w-3 mr-1" />
                  Use recommendation ({equityRecommendation.toLocaleString()})
                </Button>
              )}
            </div>
            <Input
              id="shares"
              type="number"
              step="1"
              min="1"
              {...form.register('shares_offered', { valueAsNumber: true })}
            />
            {form.formState.errors.shares_offered && (
              <p className="text-sm text-red-600">
                {form.formState.errors.shares_offered.message}
              </p>
            )}
            {shareStats && (
              <p className="text-xs text-gray-500">
                Current total outstanding: {shareStats.reduce((sum: number, stat: any) => sum + Number(stat.outstanding_shares), 0).toLocaleString()} shares
              </p>
            )}
            {equityRecommendation && form.watch('shares_offered') > 0 && (
              <div className="flex items-start space-x-1 text-xs text-gray-500">
                <Info className="h-3 w-3 mt-0.5 flex-shrink-0" />
                <span>
                  {form.watch('shares_offered') > equityRecommendation * 1.5 
                    ? 'This is significantly above the recommended amount based on contributions'
                    : form.watch('shares_offered') < equityRecommendation * 0.5
                    ? 'This is significantly below the recommended amount based on contributions'
                    : 'This aligns with contribution-based recommendations'}
                </span>
              </div>
            )}
          </div>

          {/* Show contribution snapshot when user is selected */}
          {form.watch('user_id') && showContributions && (
            <div className="border rounded-lg p-4 bg-gray-50">
              <div className="flex justify-between items-center mb-3">
                <h4 className="text-sm font-medium">Contribution Analysis</h4>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowContributions(!showContributions)}
                  className="text-xs"
                >
                  Hide
                </Button>
              </div>
              <ContributionSnapshot 
                userId={form.watch('user_id')} 
                onDataLoaded={handleContributionDataLoaded}
              />
            </div>
          )}

          {form.watch('user_id') && !showContributions && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowContributions(true)}
              className="w-full"
            >
              <Calculator className="h-4 w-4 mr-2" />
              Show Contribution Analysis
            </Button>
          )}

          <div className="space-y-2">
            <Label htmlFor="equityClass">Equity Class</Label>
            <Select
              value={form.watch('equity_class_id')}
              onValueChange={(value) => form.setValue('equity_class_id', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select equity class" />
              </SelectTrigger>
              <SelectContent>
                {equityClasses.map((equityClass) => (
                  <SelectItem key={equityClass.id} value={equityClass.id}>
                    {equityClass.class_name}
                    {equityClass.description && (
                      <span className="text-xs text-gray-500 ml-2">
                        - {equityClass.description}
                      </span>
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {form.formState.errors.equity_class_id && (
              <p className="text-sm text-red-600">
                {form.formState.errors.equity_class_id.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="vesting">Vesting Schedule</Label>
            <Select
              value={form.watch('vesting_schedule_id')}
              onValueChange={(value) => form.setValue('vesting_schedule_id', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select vesting terms" />
              </SelectTrigger>
              <SelectContent>
                {vestingSchedules.map((schedule) => (
                  <SelectItem key={schedule.id} value={schedule.id.toString()}>
                    {schedule.schedule_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {form.formState.errors.vesting_schedule_id && (
              <p className="text-sm text-red-600">
                {form.formState.errors.vesting_schedule_id.message}
              </p>
            )}
          </div>

          {selectedSchedule && (
            <div className="bg-gray-50 p-4 rounded-md text-sm space-y-1">
              <p><strong>Duration:</strong> {selectedSchedule.duration_months} months</p>
              <p><strong>Cliff:</strong> {selectedSchedule.cliff_months || 0} months</p>
              <p><strong>Vesting:</strong> {selectedSchedule.vesting_frequency || 'Monthly'}</p>
            </div>
          )}

          {/* Show option-specific fields when Options are selected */}
          {form.watch('equity_class_id') === '3' && (
            <>
              <div className="space-y-2">
                <Label htmlFor="optionType">Option Type</Label>
                <Select
                  value={form.watch('option_type') || ''}
                  onValueChange={(value) => form.setValue('option_type', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select option type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ISO">ISO (Incentive Stock Option)</SelectItem>
                    <SelectItem value="NSO">NSO (Non-Qualified Stock Option)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500">
                  ISOs have tax advantages but are limited to employees. NSOs can be granted to anyone.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="strikePrice">Strike Price per Share</Label>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-500">$</span>
                  <Input
                    id="strikePrice"
                    type="number"
                    step="0.01"
                    min="0"
                    {...form.register('strike_price', { valueAsNumber: true })}
                    placeholder={latestValuation ? `FMV: $${latestValuation.common_share_price}` : '0.00'}
                  />
                </div>
                {latestValuation && (
                  <p className="text-xs text-gray-500">
                    Latest 409A fair market value: ${latestValuation.common_share_price} per share
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="exerciseWindow">Exercise Window After Termination</Label>
                <div className="flex items-center space-x-2">
                  <Input
                    id="exerciseWindow"
                    type="number"
                    step="1"
                    min="1"
                    max="120"
                    {...form.register('exercise_window_months', { valueAsNumber: true })}
                    placeholder="90"
                    defaultValue={90}
                  />
                  <span className="text-sm text-gray-500">months</span>
                </div>
                <p className="text-xs text-gray-500">
                  Time allowed to exercise options after leaving the company (typically 90 days/3 months)
                </p>
              </div>
            </>
          )}

          {/* Show 83(b) election option for restricted stock */}
          {(form.watch('equity_class_id') === '1' || form.watch('equity_class_id') === '2') && (
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="has83b"
                  checked={form.watch('has_83b_election')}
                  onCheckedChange={(checked: boolean) => form.setValue('has_83b_election', checked)}
                />
                <Label htmlFor="has83b" className="text-sm font-normal cursor-pointer">
                  Eligible for 83(b) election
                </Label>
              </div>
              <p className="text-xs text-gray-500">
                An 83(b) election allows the recipient to pay taxes on the fair market value at grant rather than at vesting
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="explanation">Offer Explanation</Label>
            <Textarea
              id="explanation"
              {...form.register('explanation')}
              placeholder="Explain the rationale for this equity grant, expected contributions, and any special considerations..."
              rows={4}
            />
            {form.formState.errors.explanation && (
              <p className="text-sm text-red-600">{form.formState.errors.explanation.message}</p>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create Offer
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}